import { useEffect, useState, Fragment } from "react";
import { MapPin, Trophy, Calendar } from "lucide-react";
import { NEXT_MATCH } from "@/data/teamData";

function useCountdown(targetIso) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const target = new Date(targetIso).getTime();
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff / 3600000) % 24);
      const m = Math.floor((diff / 60000) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setT({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return t;
}

export default function NextMatch() {
  const { d, h, m, s } = useCountdown(NEXT_MATCH.date);

  const cells = [
    { v: String(d).padStart(2, "0"), l: "HARI" },
    { v: String(h).padStart(2, "0"), l: "JAM" },
    { v: String(m).padStart(2, "0"), l: "MENIT" },
    { v: String(s).padStart(2, "0"), l: "DETIK" },
  ];

  return (
    <section id="pertandingan" data-testid="next-match-section" className="bg-white text-[#050B14]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <div className="text-xs font-bold tracking-[0.3em] text-[#E63946] mb-3">
              SIAP TEMPUR
            </div>
            <h2 className="font-display text-5xl sm:text-6xl leading-none">
              LAGA <span className="text-[#E63946]">BERIKUTNYA</span>
            </h2>
            <p className="mt-4 text-sm text-[#050B14]/70 max-w-md">
              Hitung mundur menuju matchday. Saksikan langsung di lapangan.
            </p>
          </div>

          {/* Countdown */}
          <div data-testid="countdown" className="grid grid-cols-4 gap-2 sm:gap-3">
            {cells.map((c, i) => (
              <div
                key={i}
                className="bg-[#050B14] text-white p-3 sm:p-4 min-w-[64px] text-center"
              >
                <div className="font-display text-3xl sm:text-4xl leading-none">{c.v}</div>
                <div className="text-[9px] tracking-[0.2em] text-white/60 mt-2">{c.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Match card */}
        <div className="bg-[#F6F7F9] border border-[#050B14]/10 p-6 sm:p-10 relative overflow-hidden">
          {/* Top meta */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8 text-xs font-semibold tracking-wider text-[#050B14]/70">
            <span className="flex items-center gap-2"><Trophy size={14} className="text-[#E63946]" /> {NEXT_MATCH.competition}</span>
            <span className="flex items-center gap-2"><MapPin size={14} className="text-[#E63946]" /> {NEXT_MATCH.venue}</span>
            <span className="flex items-center gap-2"><Calendar size={14} className="text-[#E63946]" /> {NEXT_MATCH.dateLabel}</span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-center gap-6 sm:gap-4">
            {NEXT_MATCH.teams.map((team, i) => (
              <Fragment key={i}>
                <div className="flex flex-col items-center text-center gap-3 sm:flex-1">
                  {team.logo ? (
                    <img
                      src={team.logo}
                      alt={team.name}
                      className="w-24 h-24 object-contain"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-[#0A1128] flex items-center justify-center font-display text-3xl text-white">
                      {team.short}
                    </div>
                  )}
                  <div>
                    <div className="font-display text-2xl">{team.name}</div>
                    <div
                      className={`text-[10px] tracking-[0.3em] font-bold ${
                        i === 0 ? "text-[#E63946]" : "text-[#050B14]/50"
                      }`}
                    >
                      {team.role}
                    </div>
                  </div>
                </div>
                {i < NEXT_MATCH.teams.length - 1 && (
                  <div className="font-display text-5xl sm:text-6xl text-[#050B14]/20 sm:h-24 flex items-center justify-center shrink-0">VS</div>
                )}
              </Fragment>
            ))}
          </div>

          {/* CTA */}
          <div className="flex justify-center mt-10">
            <a
              href="#kontak"
              data-testid="next-match-cta"
              className="bg-[#E63946] hover:bg-[#D62839] text-white px-8 py-3 font-bold tracking-wider text-xs transition-colors"
            >
              DUKUNG TIM
            </a>
          </div>

          {/* Decoration */}
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#E63946]/5 rotate-45" />
        </div>
      </div>
    </section>
  );
}
